/*
  This component is a wrapper around the Google Maps JavaScript API loader.
  It loads the Google Maps and Marker libraries and provides them to its children via a context.
  A consumer of this context can use the `useMapsLibrary` hook to access the loaded library.
*/
import { Loader } from "@googlemaps/js-api-loader";
import React from "react";

interface APIProviderProps {
  apiKey: string;
  children: React.ReactNode;
  language?: string;
  load?: boolean;
}

export const GoogleAPIProvider: React.FC<APIProviderProps> = ({
  apiKey,
  children,
  language = "en",
  load = true
}) => {
  const [apiIsLoading, setApiIsLoading] = React.useState(load);
  const [googleMapsLibrary, setGoogleMapsLibrary] = React.useState<google.maps.MapsLibrary | null>(
    null
  );
  const [markerLibrary, setMarkerLibrary] = React.useState<google.maps.MarkerLibrary | null>(null);

  const loadMapsLibrary = React.useCallback(async () => {
    const loader = new Loader({
      apiKey,
      language,
      version: "quarterly"
    });

    const maps = (await loader.importLibrary("maps")) as google.maps.MapsLibrary;
    setGoogleMapsLibrary(maps);
  }, [apiKey, language]);

  const loadMarkerLibrary = React.useCallback(async () => {
    const loader = new Loader({
      apiKey,
      language,
      version: "quarterly"
    });

    const marker = (await loader.importLibrary("marker")) as google.maps.MarkerLibrary;
    setMarkerLibrary(marker);
  }, [apiKey, language]);

  React.useEffect(() => {
    if (!load || (googleMapsLibrary && markerLibrary)) {
      return;
    }

    setApiIsLoading(true);
    Promise.all([loadMapsLibrary(), loadMarkerLibrary()]).finally(() => {
      setApiIsLoading(false);
    });
  }, [load, loadMapsLibrary, loadMarkerLibrary, googleMapsLibrary, markerLibrary]);

  return (
    <GoogleMapsLibraryContext.Provider
      value={{ map: googleMapsLibrary, marker: markerLibrary, apiIsLoading }}
    >
      {children}
    </GoogleMapsLibraryContext.Provider>
  );
};

interface GoogleMapsLibraryContextValue {
  marker: google.maps.MarkerLibrary | null;
  map: google.maps.MapsLibrary | null;
  apiIsLoading: boolean;
}

// Create a context to provide the loaded google maps library
const GoogleMapsLibraryContext = React.createContext<GoogleMapsLibraryContextValue>({
  marker: null,
  map: null,
  apiIsLoading: false
});

// Hooks to easily access the loaded google maps library
export const useMapsLibrary = () => {
  const context = React.useContext(GoogleMapsLibraryContext);
  if (context === null) {
    throw new Error("useGoogleMaps must be used within a GoogleAPIProvider");
  }
  return context.map;
};

// Hooks to easily access the loaded google marker library
export const useMarkerLibrary = () => {
  const context = React.useContext(GoogleMapsLibraryContext);
  if (context === null) {
    throw new Error("useMarkerLibrary must be used within a GoogleAPIProvider");
  }
  return context.marker;
};
